import { Link } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import styled from 'styled-components'

import Avatar from '@/components/Avatar'
import type { BreadcrumbT } from '@/components/Breadcrumbs'
import Breadcrumbs from '@/components/Breadcrumbs'
import H1 from '@/components/H1'
import { HeaderPill } from '@/components/HeaderPill'
import { ReferenceTierPill } from '@/components/ReferenceTierPill'
import type { Author } from '@/lib/apollo/model/author'
import type { ApplicantType, ReferenceTier } from '@/lib/apollo/query/get-applicant'
import { formatDate, parseDate } from '@/utils/date'
import { toSubjectTypeLabel } from '@/utils/subjectType'

import { SkeletonHeader } from './skeleton'

export interface Props {
  avatar?: string
  name?: string
  subjectId?: string
  isLoading: boolean
  hasError: boolean
  breadcrumbs: BreadcrumbT[]
  applicantType?: ApplicantType
  applicantTier?: ReferenceTier
  lastProcessedBy?: Author
  lastProcessedAt?: string
}

export const HeaderContainer = styled.div<{ $showPills?: boolean }>`
  display: block;
  background: #ebf3fd;
  min-height: 100px;
  padding: 10px 20px;
  margin-bottom: ${({ $showPills }) => ($showPills ? '30px' : '10px')};
`

const CopyLink = styled.a`
  color: #41a7f5;
  text-decoration: underline;
  cursor: pointer;
`

const IDContainer = styled.span`
  color: #55616b;
`

const onClickSubjectId = (subjectId: string) => {
  navigator.clipboard.writeText(subjectId)
}

export const SummaryContainer = styled.div`
  display: grid;
  grid-template-columns: min-content 2fr 1fr;
  grid-gap: 10px;
  padding: 20px 0;
`

export const SummaryInnerContainer = styled.div`
  display: grid;
  grid-gap: 5px;
  grid-template-rows: min-content 1fr;
`

export const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  justify-content: center;
  align-items: flex-end;
`

const PillsContainer = styled.div`
  display: block;
  position: relative;
  padding-left: 50px;
  margin-bottom: -25px;
`

export const Header = ({
  isLoading,
  hasError,
  breadcrumbs,
  avatar,
  name,
  subjectId,
  applicantType,
  applicantTier,
  lastProcessedBy,
  lastProcessedAt,
}: Props) => {
  const skeleton = subjectId === undefined || isLoading || hasError
  const showPills = applicantTier !== undefined || applicantType !== undefined

  if (skeleton) {
    return <SkeletonHeader />
  }

  return (
    <HeaderContainer $showPills={showPills}>
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <SummaryContainer>
        <Avatar url={avatar} name={name || 'VC'} />
        <SummaryInnerContainer>
          <H1>{name}</H1>
          <IDContainer>
            <span>{subjectId}</span>{' '}
            <CopyLink
              onClick={() => {
                if (subjectId !== undefined) {
                  onClickSubjectId(subjectId)
                }
              }}
            >
              <em>(Copy)</em>
            </CopyLink>
          </IDContainer>
        </SummaryInnerContainer>
        <ActionsContainer>
          {lastProcessedBy !== undefined && (
            <>
              <span>
                Owner:{' '}
                <Link to={`/executive/${lastProcessedBy.userId}`} component={RouterLink}>
                  {lastProcessedBy.fullName}
                </Link>
              </span>
              {lastProcessedAt && (
                <span>
                  Last assigned at:{' '}
                  {formatDate(parseDate(lastProcessedAt), 'dd/MM/yyyy hh:mmaaa')}
                </span>
              )}
            </>
          )}
        </ActionsContainer>
      </SummaryContainer>
      {showPills && (
        <PillsContainer>
          {applicantTier && <ReferenceTierPill applicantTier={applicantTier} />}
          {applicantType && <HeaderPill label={toSubjectTypeLabel(applicantType)} />}
        </PillsContainer>
      )}
    </HeaderContainer>
  )
}
